/**
 * Copyright (c) Qwoach LLC 2026. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 */

import {defaultColorPickerPalette} from "@/core/js/PaletteEnum";
import SessionPlanItem from "@shared/Journal/SessionPlanItem";
import {SessionPlanDefaultItemIdEnum} from "@shared/Journal/SessionPlanDefaultItemIdEnum";
import {SessionPlanItemTypeEnum} from "@shared/Journal/SessionPlanItemTypeEnum";
import {SessionPlanRollForwardEnum} from "@shared/Journal/SessionPlanRollForwardEnum";
import {JournalEntryType} from "@shared/Journal/JournalEntryType";

function createTags(items = [], color = null){
	return (items || []).map(item => ({
		title: item.title,
		description: item.description,
		color,
	}))
}

function createSessionPlanItem(options = {}){
	return new SessionPlanItem(options).setId(options.id)
}

const SessionPlanDefaultTitleIdMapByEntryType = Object.freeze({
	[JournalEntryType.TLSW_SESSION_PLAN]: Object.freeze({
		"Aims for This Session": SessionPlanDefaultItemIdEnum.TLSW_AIMS,
		"How am I Going to Achieve This in This Session?": SessionPlanDefaultItemIdEnum.TLSW_HOW,
		"Tools/Techniques I Plan to Use": SessionPlanDefaultItemIdEnum.TLSW_TOOLS,
		"Session Materials I need to take": SessionPlanDefaultItemIdEnum.TLSW_MATERIALS,
		"Risk Assessment/Potential": SessionPlanDefaultItemIdEnum.TLSW_RISK,
		"Outcome": SessionPlanDefaultItemIdEnum.TLSW_OUTCOME,
		"Evaluation": SessionPlanDefaultItemIdEnum.TLSW_EVALUATION,
		"Ongoing Reflections": SessionPlanDefaultItemIdEnum.TLSW_REFLECTIONS,
	}),
	[JournalEntryType.BEHAVIOUR_TREE_SESSION_PLAN]: Object.freeze({
		"Leaves": SessionPlanDefaultItemIdEnum.BT_LEAVES,
		"Roots": SessionPlanDefaultItemIdEnum.BT_ROOTS,
		"Discussion": SessionPlanDefaultItemIdEnum.BT_DISCUSSION,
		"Tools/Techniques I Plan to Use": SessionPlanDefaultItemIdEnum.BT_TOOLS,
		"Session Materials I need to take": SessionPlanDefaultItemIdEnum.BT_MATERIALS,
	}),
})

export function getStableDefaultSessionPlanItemId(entryType, title) {
	return SessionPlanDefaultTitleIdMapByEntryType[entryType]?.[title] || null
}


export function createDefaultTLSWSessionPlanItems({tools = [], materials = []} = {}){
	return [
		createSessionPlanItem({
			id: SessionPlanDefaultItemIdEnum.TLSW_AIMS,
			title: "Aims for This Session",
			description: "Set out your objectives for this intervention at the outset – what am I trying to achieve? Then ‘how am I going to achieve them? That approach ensures focus and avoids drift",
			itemType: SessionPlanItemTypeEnum.TEXT,
			rollForward: SessionPlanRollForwardEnum.ONCE,
		}),
		createSessionPlanItem({
			id: SessionPlanDefaultItemIdEnum.TLSW_HOW,
			title: "How am I Going to Achieve This in This Session?",
			description: "Set out your objectives for this intervention at the outset – what am I trying to achieve? Then ‘how am I going to achieve them? That approach ensures focus and avoids drift",
			itemType: SessionPlanItemTypeEnum.TEXT,
		}),
		createSessionPlanItem({
			id: SessionPlanDefaultItemIdEnum.TLSW_TOOLS,
			title: "Tools/Techniques I Plan to Use",
			itemType: SessionPlanItemTypeEnum.TAGS,
			tags: createTags(tools, defaultColorPickerPalette[0]),
			defaultTagColor: defaultColorPickerPalette[0],
		}),
		createSessionPlanItem({
			id: SessionPlanDefaultItemIdEnum.TLSW_MATERIALS,
			title: "Session Materials I need to take",
			itemType: SessionPlanItemTypeEnum.TAGS,
			tags: createTags(materials, defaultColorPickerPalette[1]),
			defaultTagColor: defaultColorPickerPalette[1],
		}),
		createSessionPlanItem({
			id: SessionPlanDefaultItemIdEnum.TLSW_RISK,
			title: "Risk Assessment/Potential",
			description: "From Behaviour Tree Session",
			itemType: SessionPlanItemTypeEnum.TEXT,
			rollForward: SessionPlanRollForwardEnum.ALL,
		}),
		createSessionPlanItem({
			id: SessionPlanDefaultItemIdEnum.TLSW_OUTCOME,
			title: "Outcome",
			description: "Were the aims met? If not, why not?",
			itemType: SessionPlanItemTypeEnum.TEXT,
			rollForward: SessionPlanRollForwardEnum.ONCE,
		}),
		createSessionPlanItem({
			id: SessionPlanDefaultItemIdEnum.TLSW_EVALUATION,
			title: "Evaluation",
			description: "Was content pitched to meet the child’s level of understanding? What went well? What improvements can be made? Any future recommendations?",
			itemType: SessionPlanItemTypeEnum.TEXT,
		}),
		createSessionPlanItem({
			id: SessionPlanDefaultItemIdEnum.TLSW_REFLECTIONS,
			title: "Ongoing Reflections",
			description: "Include your Reflections / Feedback / Supervision / Research / Notes",
			itemType: SessionPlanItemTypeEnum.TEXT,
			rollForward: SessionPlanRollForwardEnum.ALL,
		}),
	]
}

export function createDefaultBTSessionPlanItems({tools = [], materials = []} = {}){
	return [
		createSessionPlanItem({
			id: SessionPlanDefaultItemIdEnum.BT_LEAVES,
			title: "Leaves",
			itemType: SessionPlanItemTypeEnum.TEXT,
		}),
		createSessionPlanItem({
			id: SessionPlanDefaultItemIdEnum.BT_ROOTS,
			title: "Roots",
			itemType: SessionPlanItemTypeEnum.TEXT,
		}),
		createSessionPlanItem({
			id: SessionPlanDefaultItemIdEnum.BT_DISCUSSION,
			title: "Discussion",
			itemType: SessionPlanItemTypeEnum.TEXT,
		}),
		createSessionPlanItem({
			id: SessionPlanDefaultItemIdEnum.BT_TOOLS,
			title: "Tools/Techniques I Plan to Use",
			itemType: SessionPlanItemTypeEnum.TAGS,
			tags: createTags(tools, defaultColorPickerPalette[0]),
			defaultTagColor: defaultColorPickerPalette[0],
		}),
		createSessionPlanItem({
			id: SessionPlanDefaultItemIdEnum.BT_MATERIALS,
			title: "Session Materials I need to take",
			itemType: SessionPlanItemTypeEnum.TAGS,
			tags: createTags(materials, defaultColorPickerPalette[1]),
			defaultTagColor: defaultColorPickerPalette[1],
		}),
	]
}

export function createDefaultSessionPlanItems(entryType, options = {}){
	switch (entryType) {
		case JournalEntryType.BEHAVIOUR_TREE_SESSION_PLAN:
			return createDefaultBTSessionPlanItems(options)
		case JournalEntryType.TLSW_SESSION_PLAN:
		default:
			return createDefaultTLSWSessionPlanItems(options)
	}
}
